import type { ReactNode } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import Tooltip from 'LuxKit/Tooltip'
import Badge from 'LuxKit/Badge'
import Group from 'components/utils/Group'

import { toBaseFareClass, type FareClassWithReward } from 'constants/flight'
import { CHARTER_CABIN_CONTENT, LineCarIcon } from './charterCabinContent'

interface Props {
  fareClass: FareClassWithReward
  section: 'inclusions' | 'baggage'
  /**
   * When set, render only the first `maxItems`. The parent renders any "Show more" toggle
   * (this component intentionally stays presentational so callers control truncation UI).
   * Surfaces that always show every item (e.g. the fare-class modal cards) simply omit it.
   */
  maxItems?: number
}

/**
 * Renders the per-cabin amenity list (inclusions or baggage) sourced from
 * {@link CHARTER_CABIN_CONTENT}. Used by the fare-class modal cards and the
 * trip-options-table expanded row so the two surfaces share copy + icons.
 */
function CharterCabinFeatureList({ fareClass, section, maxItems }: Props) {
  const intl = useIntl()
  const content = CHARTER_CABIN_CONTENT[toBaseFareClass(fareClass)]
  if (!content) return null

  const allItems = section === 'inclusions' ? content.inclusions : content.baggage
  const items = maxItems != null ? allItems.slice(0, maxItems) : allItems

  return <>
    {items.map((amenity, i) => {
      if (amenity.icon === LineCarIcon) {
        return (
          <CaptionTextBlock
            key={i}
            variant="medium"
            startIcon={<amenity.icon />}
            endIcon={
              <Tooltip
                description={intl.formatMessage({
                  id: '9YhUIqIqMuGyui3siwDv',
                  defaultMessage: 'SYD/BNE must book domestic flights with their charter package to be eligible',
                })}
                placement="top"
              >
                <LineInfoCircleIcon colour="neutral-two" size={14} />
              </Tooltip>
            }
          >
            <FormattedMessage
              id="Y-Dw8m2rFgKcyn5hy7J5"
              defaultMessage="<badge>New</badge> Home to airport transfers (max 40km) in MEL/SYD/BNE"
              
              values={{
                badge: (chunks: ReactNode) => (
                  <Group direction="horizontal" verticalAlign="center" gap={0}>
                    <Badge mode="critical" kind="string">{chunks}</Badge>
                  </Group>
                ),
              }}
            />
          </CaptionTextBlock>
        )
      }

      return (
        <CaptionTextBlock key={i} variant="medium" startIcon={<amenity.icon />}>
          {intl.formatMessage(amenity.message)}
        </CaptionTextBlock>
      )
    })}
  </>
}

export default CharterCabinFeatureList

/** Total number of items the cabin would render for the given section — lets callers decide
 *  whether a "Show more" toggle is worth rendering without duplicating the data lookup. */
export function getCharterCabinFeatureCount(
  fareClass: FareClassWithReward,
  section: 'inclusions' | 'baggage',
): number {
  const content = CHARTER_CABIN_CONTENT[toBaseFareClass(fareClass)]
  if (!content) return 0
  return (section === 'inclusions' ? content.inclusions : content.baggage).length
}
